/**
 * Central icon module (PORTAL-CORRECTIONS-CONTRACT §3, feedback #3).
 *
 * The ONE place portal icons are chosen. Maps a small set of *semantic* names to
 * HugeIcons SVG data objects (`@hugeicons/core-free-icons`) rendered through
 * `@hugeicons/react`'s `<HugeiconsIcon>`. Call-sites use `<Icon name="tasks" />`
 * or named exports from this file, so the visible UI (sidebar nav, header,
 * page headers, table/row actions, assistant composer) stays HugeIcons-only and
 * maintainable from here.
 *
 * Note on types: the `IconSvgElement` type is exported by `@hugeicons/react`
 * (the core-free package types its icon constants as the structurally-compatible
 * `IconSvgObject`, and does NOT export a named element type). We import the type
 * from `@hugeicons/react`.
 */
import * as React from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  // brand + chrome
  Hexagon01Icon,
  Search01Icon,
  Menu01Icon,
  Sun01Icon,
  Moon02Icon,
  ComputerIcon,
  UserIcon,
  Logout01Icon,
  SparklesIcon,
  Cancel01Icon,
  // row / table actions
  MoreHorizontalIcon,
  PlusSignIcon,
  PencilEdit01Icon,
  Delete02Icon,
  ArrowUp01Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  // nav (one per NAV_ITEMS entry)
  DashboardSquare01Icon,
  FolderLibraryIcon,
  TaskDone01Icon,
  Target01Icon,
  File01Icon,
  Files01Icon,
  Compass01Icon,
  RadioIcon,
  Layers01Icon,
  TradeUpIcon,
  TelescopeIcon,
  AtomIcon,
  GitBranchIcon,
  JusticeScale01Icon,
  ClipboardListIcon,
  Shield01Icon,
  Activity01Icon,
  TimeQuarter02Icon,
  BotIcon,
  GitPullRequestIcon,
  WorkflowSquare01Icon,
  Building01Icon,
  UserMultiple02Icon,
  SquareLock01Icon,
  BookOpen01Icon,
  Share08Icon,
  // assistant composer
  SentIcon,
  StopCircleIcon,
  // assistant rail (tool-call / result / context scope)
  Wrench01Icon,
  CheckmarkCircle02Icon,
  Tick02Icon,
  EraserIcon,
  BoxesIcon,
  AiChat01Icon,
  Alert02Icon,
  Archive02Icon,
  ArrowDownLeft01Icon,
  ArrowTurnBackwardIcon,
  ArrowUpRight01Icon,
  CalendarCheckIn01Icon,
  CheckListIcon,
  CheckmarkBadge01Icon,
  CircleIcon,
  Copy01Icon,
  DashboardSpeed01Icon,
  DeadIcon,
  File02Icon,
  FileAddIcon,
  Flag01Icon,
  FlagOffIcon,
  FlashIcon,
  GaugeIcon,
  InboxIcon,
  InformationCircleIcon,
  LicenseIcon,
  Link01Icon,
  LinkSquare01Icon,
  Loading03Icon,
  PackageAddIcon,
  PackageIcon,
  PauseIcon,
  PlayIcon,
  PowerServiceIcon,
  RefreshIcon,
  RepeatIcon,
  ShareKnowledgeIcon,
  SourceCodeSquareIcon,
  Tag01Icon,
  Task01Icon,
  TestTube01Icon,
  UnfoldMoreIcon,
} from "@hugeicons/core-free-icons";

export const ICONS = {
  // brand + chrome
  brand: Hexagon01Icon,
  search: Search01Icon,
  menu: Menu01Icon,
  themeLight: Sun01Icon,
  themeDark: Moon02Icon,
  themeSystem: ComputerIcon,
  user: UserIcon,
  logout: Logout01Icon,
  assistant: SparklesIcon,
  close: Cancel01Icon,
  // row / table actions
  more: MoreHorizontalIcon,
  create: PlusSignIcon,
  edit: PencilEdit01Icon,
  delete: Delete02Icon,
  sortAsc: ArrowUp01Icon,
  sortDesc: ArrowDown01Icon,
  prev: ArrowLeft01Icon,
  next: ArrowRight01Icon,
  // nav (one per NAV_ITEMS entry)
  dashboard: DashboardSquare01Icon,
  projects: FolderLibraryIcon,
  tasks: TaskDone01Icon,
  okrs: Target01Icon,
  artifacts: File01Icon,
  contexts: Compass01Icon,
  signals: RadioIcon,
  packageGraph: Layers01Icon,
  bets: TradeUpIcon,
  frames: TelescopeIcon,
  principles: AtomIcon,
  skills: SparklesIcon,
  processes: GitBranchIcon,
  rules: JusticeScale01Icon,
  evidence: ClipboardListIcon,
  decisions: Shield01Icon,
  documents: Files01Icon,
  operations: Activity01Icon,
  events: TimeQuarter02Icon,
  ruleRuns: WorkflowSquare01Icon,
  agentRuns: BotIcon,
  eventSources: RadioIcon,
  effectRuns: WorkflowSquare01Icon,
  transitionProposals: GitPullRequestIcon,
  contourInstances: WorkflowSquare01Icon,
  actionCards: CheckListIcon,
  controlRoom: DashboardSpeed01Icon,
  companies: Building01Icon,
  users: UserMultiple02Icon,
  roles: SquareLock01Icon,
  docs: BookOpen01Icon,
  packages: Layers01Icon,
  graph: Share08Icon,
  // assistant composer
  send: SentIcon,
  stop: StopCircleIcon,
  clear: EraserIcon,
  // assistant rail
  assistantChat: AiChat01Icon,
  tool: Wrench01Icon,
  approve: Tick02Icon,
  reject: Cancel01Icon,
  defer: PauseIcon,
  reopen: ArrowTurnBackwardIcon,
  resultOk: CheckmarkCircle02Icon,
  deepLink: ArrowRight01Icon,
  scope: BoxesIcon,
} satisfies Record<string, IconSvgElement>;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  "data-icon"?: "inline-start" | "inline-end";
  "aria-hidden"?: boolean | "true" | "false";
}

export function Icon({
  name,
  size = 16,
  strokeWidth = 1.8,
  className,
  ...rest
}: IconProps) {
  return (
    <HugeiconsIcon
      icon={ICONS[name]}
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      {...rest}
    />
  );
}

export type IconComponentProps = Omit<
  React.ComponentPropsWithoutRef<typeof HugeiconsIcon>,
  "icon"
>;

export type IconComponent = React.ForwardRefExoticComponent<
  IconComponentProps & React.RefAttributes<SVGSVGElement>
>;

function makeIcon(icon: IconSvgElement): IconComponent {
  return React.forwardRef<SVGSVGElement, IconComponentProps>(
    function HugeiconShim(props, ref) {
      return <HugeiconsIcon ref={ref} icon={icon} {...props} />;
    },
  );
}

export const Activity = makeIcon(Activity01Icon);
export const AlertTriangle = makeIcon(Alert02Icon);
export const Archive = makeIcon(Archive02Icon);
export const ArrowDown = makeIcon(ArrowDown01Icon);
export const ArrowDownLeft = makeIcon(ArrowDownLeft01Icon);
export const ArrowLeft = makeIcon(ArrowLeft01Icon);
export const ArrowRight = makeIcon(ArrowRight01Icon);
export const ArrowUp = makeIcon(ArrowUp01Icon);
export const ArrowUpRight = makeIcon(ArrowUpRight01Icon);
export const BadgeCheck = makeIcon(CheckmarkBadge01Icon);
export const Ban = makeIcon(Cancel01Icon);
export const Bot = makeIcon(BotIcon);
export const Box = makeIcon(PackageIcon);
export const Boxes = makeIcon(BoxesIcon);
export const Building2 = makeIcon(Building01Icon);
export const CalendarCheck = makeIcon(CalendarCheckIn01Icon);
export const Check = makeIcon(Tick02Icon);
export const CheckCircle2 = makeIcon(CheckmarkCircle02Icon);
export const ChevronDown = makeIcon(ArrowDown01Icon);
export const ChevronRight = makeIcon(ArrowRight01Icon);
export const ChevronUp = makeIcon(ArrowUp01Icon);
export const ChevronsUpDown = makeIcon(UnfoldMoreIcon);
export const Circle = makeIcon(CircleIcon);
export const ClipboardCheck = makeIcon(TaskDone01Icon);
export const Compass = makeIcon(Compass01Icon);
export const Copy = makeIcon(Copy01Icon);
export const ExternalLink = makeIcon(LinkSquare01Icon);
export const FileBox = makeIcon(File01Icon);
export const FileCheck2 = makeIcon(CheckmarkCircle02Icon);
export const FileJson = makeIcon(SourceCodeSquareIcon);
export const FilePlus2 = makeIcon(FileAddIcon);
export const FileText = makeIcon(File02Icon);
export const Flag = makeIcon(Flag01Icon);
export const FlagOff = makeIcon(FlagOffIcon);
export const FlaskConical = makeIcon(TestTube01Icon);
export const FolderKanban = makeIcon(FolderLibraryIcon);
export const FolderTree = makeIcon(FolderLibraryIcon);
export const Gauge = makeIcon(GaugeIcon);
export const GaugeCircle = makeIcon(DashboardSpeed01Icon);
export const GitBranch = makeIcon(GitBranchIcon);
export const GitPullRequestArrow = makeIcon(GitPullRequestIcon);
export const Inbox = makeIcon(InboxIcon);
export const Info = makeIcon(InformationCircleIcon);
export const Layers = makeIcon(Layers01Icon);
export const Link2 = makeIcon(Link01Icon);
export const ListChecks = makeIcon(CheckListIcon);
export const ListTodo = makeIcon(Task01Icon);
export const Loader2 = makeIcon(Loading03Icon);
export const MoreHorizontal = makeIcon(MoreHorizontalIcon);
export const MoveRight = makeIcon(ArrowRight01Icon);
export const Network = makeIcon(ShareKnowledgeIcon);
export const Package = makeIcon(PackageIcon);
export const PackagePlus = makeIcon(PackageAddIcon);
export const Pause = makeIcon(PauseIcon);
export const Pencil = makeIcon(PencilEdit01Icon);
export const Play = makeIcon(PlayIcon);
export const Plus = makeIcon(PlusSignIcon);
export const Power = makeIcon(PowerServiceIcon);
export const PowerOff = makeIcon(Logout01Icon);
export const Radio = makeIcon(RadioIcon);
export const RefreshCw = makeIcon(RefreshIcon);
export const Repeat = makeIcon(RepeatIcon);
export const RotateCcw = makeIcon(ArrowTurnBackwardIcon);
export const Scale = makeIcon(JusticeScale01Icon);
export const ScrollText = makeIcon(LicenseIcon);
export const Search = makeIcon(Search01Icon);
export const Send = makeIcon(SentIcon);
export const Share2 = makeIcon(Share08Icon);
export const Shield = makeIcon(Shield01Icon);
export const Skull = makeIcon(DeadIcon);
export const Sparkles = makeIcon(SparklesIcon);
export const Tags = makeIcon(Tag01Icon);
export const Target = makeIcon(Target01Icon);
export const Trash2 = makeIcon(Delete02Icon);
export const TrendingUp = makeIcon(TradeUpIcon);
export const UserRound = makeIcon(UserIcon);
export const Users = makeIcon(UserMultiple02Icon);
export const Workflow = makeIcon(WorkflowSquare01Icon);
export const X = makeIcon(Cancel01Icon);
export const Zap = makeIcon(FlashIcon);
